import { useMemo } from 'react';

// Hooks
import moment from 'moment-timezone';
import { usePayment } from './usePayment';
import { useAppSelector } from './useAppStore';
import { useFilter } from './useFilter';

// Redux-toolkit
import { getSheets, getSelectedLocationId } from '../store/sheets/selectors';
import { getSelectedFilterItems } from '../store/common/selectors';

// Types
import { TPersonnelStatus, TTimesheetsList } from '../types';

export function useSheets() {
  const { getAmountString } = usePayment();
  const sheets = useAppSelector(getSheets);
  const selectedFilterItems = useAppSelector(getSelectedFilterItems);
  const selectedLocationId = useAppSelector(getSelectedLocationId);

  const {
    filterByLocations,
    filterByPositions,
    filterByPermissions,
    filterByDepartments,
  } = useFilter();

  const getSheetsByVisibility = (sheets: Array<TTimesheetsList>) => sheets.map((sheet) => {
    const isDisplaySalary = 'payment' in sheet;

    return {
      ...sheet,
      payment: {
        ...sheet.payment,
        paymentAmount: isDisplaySalary ? getAmountString(sheet) : '/',
        paymentType: isDisplaySalary ? sheet.payment.paymentType : '/',
      },
      totals: {
        ...sheet.totals,
        totalPay: isDisplaySalary ? sheet.totals.totalPay : '/',
      },
    };
  });

  const getTimesheetsTotals = (sheets: Array<TTimesheetsList>) => {
    let totalHour = 0;
    let totalAmount = 0;

    sheets.forEach(({ totals }: TTimesheetsList) => {
      totalHour += Number(totals?.sheets.totalHours);

      if (!totals?.totalPay?.startsWith('NaN')
        && !totals?.totalPay?.startsWith('N/A')
        && !totals?.totalPay?.startsWith('/')) totalAmount += Number(totals?.totalPay);
    });

    return {
      totalHours: totalHour?.toFixed(2),
      totalAmount: totalAmount.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    };
  };

  const getSheetsByFilters = useMemo(() => {
    let filteredTeam = sheets;

    if (selectedFilterItems.role?.length) {
      filteredTeam = filterByPermissions(sheets);
    }

    if (selectedFilterItems.department?.length) {
      filteredTeam = filterByDepartments(sheets);
    }

    if (selectedFilterItems.position?.length) {
      filteredTeam = filterByPositions(sheets);
    }

    if (selectedFilterItems.location?.length) {
      filteredTeam = filterByLocations(sheets);
    }

    if (selectedLocationId > 0) {
      filteredTeam = filteredTeam.filter((sheet) => sheet.personnel.locations.some((location) => location.id === selectedLocationId));
    }

    return filteredTeam;
  }, [selectedFilterItems, sheets, selectedLocationId]);

  const getWorkTimes = (personnelStatus: TPersonnelStatus) => {
    const shiftStart = moment(personnelStatus?.shiftStart);
    const breakStart = moment(personnelStatus?.breakStart);
    const currentTime = moment();

    const duration = moment.duration(currentTime.diff(shiftStart));
    const breakDuration = moment.duration(currentTime.diff(breakStart));

    const hours = Math.floor(duration.asHours()); // Total hours
    const minutes = duration.minutes(); // Remaining minutes
    const seconds = duration.seconds(); // Remaining seconds

    const breakHours = Math.floor(breakDuration.asHours());
    const breakMinutes = breakDuration.minutes(); // Remaining minutes
    const breakSeconds = breakDuration.seconds(); // Remaining seconds

    const formatTime = (hours: number, minutes: number, seconds: number) => {
      let formatted = '';

      if (hours > 0) {
        formatted += `${hours} ${hours > 1 ? 'hours' : 'hours'}`;
      }
      if (minutes > 0) {
        formatted += `${hours > 0 ? ' and ' : ''}${minutes} ${minutes > 1 ? 'minutes' : 'minutes'}`;
      }
      if (hours === 0 && minutes === 0 && seconds > 0) {
        formatted += `${seconds} ${seconds > 1 ? 'seconds' : 'second'}`;
      }

      return formatted;
    };

    const formattedTime = formatTime(hours, minutes, seconds);
    const formattedBreak = formatTime(breakHours, breakMinutes, breakSeconds);

    return {
      formattedTime,
      formattedBreak,
    };
  };

  return {
    getSheetsByVisibility,
    getSheetsByFilters,
    getTimesheetsTotals,
    getWorkTimes,
  };
}
